import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Upload,
  Search,
  ArrowRightLeft,
  Package,
  ImageIcon,
} from 'lucide-react';

interface Equivalency {
  id: number;
  supplier_id: number;
  category: string;
  external_code: string;
  internal_code: string;
  description: string | null;
  image_url: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
}

type Category = 'products' | 'suppliers' | 'services';

const CATEGORY_LABELS: Record<Category, string> = {
  products: 'Productos',
  suppliers: 'Proveedores',
  services: 'Servicios',
};

const CATEGORY_ICONS: Record<Category, typeof Package> = {
  products: Package,
  suppliers: ArrowRightLeft,
  services: ArrowRightLeft,
};

const PER_PAGE = 20;

export default function AdminEquivalencias() {
  const navigate = useNavigate();
  const { supplierId } = useParams<{ supplierId: string }>();
  const { usuario: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [category, setCategory] = useState<Category>('products');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; equivalency: Equivalency | null }>({ open: false, equivalency: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; equivalency: Equivalency | null }>({ open: false, equivalency: null });
  const [importModal, setImportModal] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Create form
  const [createForm, setCreateForm] = useState({ external_code: '', internal_code: '', description: '', image_url: '' });
  // Edit form
  const [editForm, setEditForm] = useState({ external_code: '', internal_code: '', description: '', image_url: '' });
  // Import
  const [importText, setImportText] = useState('');
  const [importFormat, setImportFormat] = useState<'csv' | 'json'>('csv');
  const [importResult, setImportResult] = useState<{ imported: number; total_submitted: number; errors: any[] } | null>(null);

  const supplierIdNum = Number(supplierId);

  // Fetch supplier details
  const { data: supplierData, isLoading: supplierLoading } = useQuery({
    queryKey: ['supplier', supplierIdNum],
    queryFn: () => api.getSupplier(supplierIdNum),
    enabled: !!supplierId && !isNaN(supplierIdNum),
  });

  const supplier = supplierData?.data;

  // Fetch equivalencies
  const { data: eqData, isLoading: eqLoading } = useQuery({
    queryKey: ['equivalencias', supplierIdNum, category, page, search],
    queryFn: () => api.getEquivalencias({
      supplier_id: supplierIdNum,
      category,
      page,
      per_page: PER_PAGE,
      search: search || undefined,
    }),
    enabled: !!supplierId && !isNaN(supplierIdNum),
  });

  const equivalencias: Equivalency[] = eqData?.data || [];
  const totalPages = eqData?.total_pages || 1;
  const total = eqData?.total || 0;

  const invalidateEquivalencias = () => {
    queryClient.invalidateQueries({ queryKey: ['equivalencias', supplierIdNum, category] });
  };

  // --- Search ---
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // --- Category change ---
  const handleCategoryChange = (cat: Category) => {
    setCategory(cat);
    setPage(1);
    setSearch('');
    setSearchInput('');
  };

  // --- Create ---
  const handleCreate = async () => {
    setError('');
    if (!createForm.external_code || !createForm.internal_code) {
      setError('Codigo externo y codigo interno son requeridos');
      return;
    }

    setIsProcessing(true);
    try {
      await api.createEquivalencia({
        supplier_id: supplierIdNum,
        category,
        external_code: createForm.external_code.trim(),
        internal_code: createForm.internal_code.trim(),
        description: createForm.description.trim() || undefined,
        image_url: createForm.image_url.trim() || undefined,
      });
      invalidateEquivalencias();
      setCreateModal(false);
      setCreateForm({ external_code: '', internal_code: '', description: '', image_url: '' });
    } catch (err: any) {
      setError(err.message || 'Error al crear equivalencia');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Edit ---
  const openEditModal = (eq: Equivalency) => {
    setEditForm({
      external_code: eq.external_code,
      internal_code: eq.internal_code,
      description: eq.description || '',
      image_url: eq.image_url || '',
    });
    setEditModal({ open: true, equivalency: eq });
    setError('');
  };

  const handleEdit = async () => {
    if (!editModal.equivalency) return;
    setError('');

    if (!editForm.external_code || !editForm.internal_code) {
      setError('Codigo externo y codigo interno son requeridos');
      return;
    }

    setIsProcessing(true);
    try {
      await api.updateEquivalencia(editModal.equivalency.id, {
        external_code: editForm.external_code.trim(),
        internal_code: editForm.internal_code.trim(),
        description: editForm.description.trim() || null,
        image_url: editForm.image_url.trim() || null,
      });
      invalidateEquivalencias();
      setEditModal({ open: false, equivalency: null });
    } catch (err: any) {
      setError(err.message || 'Error al actualizar equivalencia');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Delete ---
  const handleDelete = async () => {
    if (!deleteModal.equivalency) return;
    setIsProcessing(true);
    try {
      await api.deleteEquivalencia(deleteModal.equivalency.id);
      invalidateEquivalencias();
      setDeleteModal({ open: false, equivalency: null });
    } catch (err: any) {
      setError(err.message || 'Error al eliminar equivalencia');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Import ---
  const parseImportData = (): Array<{ external_code: string; internal_code: string; description?: string }> | null => {
    const text = importText.trim();
    if (!text) {
      setError('Pegue los datos a importar');
      return null;
    }

    if (importFormat === 'json') {
      try {
        const parsed = JSON.parse(text);
        const items = Array.isArray(parsed) ? parsed : [parsed];
        for (const item of items) {
          if (!item.external_code || !item.internal_code) {
            setError('Cada elemento JSON debe tener external_code e internal_code');
            return null;
          }
        }
        return items.map((item: any) => ({
          external_code: String(item.external_code).trim(),
          internal_code: String(item.internal_code).trim(),
          description: item.description ? String(item.description).trim() : undefined,
        }));
      } catch {
        setError('El texto no es JSON valido');
        return null;
      }
    }

    // CSV format: external_code,internal_code,description (one per line)
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const items: Array<{ external_code: string; internal_code: string; description?: string }> = [];

    for (let i = 0; i < lines.length; i++) {
      const parts = lines[i].split(',').map(p => p.trim());
      if (parts.length < 2) {
        setError(`Linea ${i + 1}: se requieren al menos 2 columnas (codigo_externo, codigo_interno)`);
        return null;
      }
      items.push({
        external_code: parts[0],
        internal_code: parts[1],
        description: parts[2] || undefined,
      });
    }

    return items;
  };

  const handleImport = async () => {
    setError('');
    setImportResult(null);

    const items = parseImportData();
    if (!items) return;

    if (!supplier?.code && !supplier?.nit) {
      setError('No se pudo determinar el codigo del proveedor');
      return;
    }

    setIsProcessing(true);
    try {
      const supplierCode = supplier.code || supplier.nit;
      const result = await api.importEquivalencias(supplierCode, category, items);
      setImportResult(result.data);
      invalidateEquivalencias();
    } catch (err: any) {
      setError(err.message || 'Error al importar equivalencias');
    } finally {
      setIsProcessing(false);
    }
  };

  const closeImportModal = () => {
    setImportModal(false);
    setImportText('');
    setImportResult(null);
    setError('');
    setImportFormat('csv');
  };

  // --- Access control ---
  if (currentUser?.rol !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Acceso denegado</h2>
          <p className="text-muted-foreground mt-2">Solo los administradores pueden acceder a esta pagina.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/suppliers')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <ArrowRightLeft className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold">
                    Equivalencias
                    {supplier && !supplierLoading && (
                      <span className="text-muted-foreground font-normal"> - {supplier.name || supplier.razon_social}</span>
                    )}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {supplier?.nit && `NIT: ${supplier.nit}`}
                    {supplier?.code && supplier?.nit && ' | '}
                    {supplier?.code && `Codigo: ${supplier.code}`}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => { setImportModal(true); setError(''); }}>
                <Upload className="h-4 w-4 mr-2" />
                Importar
              </Button>
              <Button onClick={() => { setCreateModal(true); setError(''); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Equivalencia
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Tabs */}
        <div className="flex items-center gap-2 mb-6">
          {(Object.keys(CATEGORY_LABELS) as Category[]).map((cat) => {
            const Icon = CATEGORY_ICONS[cat];
            return (
              <Button
                key={cat}
                variant={category === cat ? 'default' : 'outline'}
                onClick={() => handleCategoryChange(cat)}
                className="gap-2"
              >
                <Icon className="h-4 w-4" />
                {CATEGORY_LABELS[cat]}
              </Button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="flex items-center gap-2 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por codigo externo o interno..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={handleSearch}>
            Buscar
          </Button>
          {search && (
            <Button
              variant="ghost"
              onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
            >
              Limpiar
            </Button>
          )}
          <div className="ml-auto text-sm text-muted-foreground">
            {total} equivalencia{total !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow">
          {supplierLoading || eqLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Cargando equivalencias...</p>
            </div>
          ) : equivalencias.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {search
                  ? `No se encontraron equivalencias para "${search}"`
                  : `No hay equivalencias de ${CATEGORY_LABELS[category].toLowerCase()} registradas para este proveedor`
                }
              </p>
              <Button className="mt-4" onClick={() => { setCreateModal(true); setError(''); }}>
                <Plus className="h-4 w-4 mr-2" />
                Crear primera equivalencia
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">Img</TableHead>
                    <TableHead>Codigo Externo</TableHead>
                    <TableHead>Codigo Interno</TableHead>
                    <TableHead>Descripcion</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {equivalencias.map((eq) => (
                    <TableRow key={eq.id}>
                      <TableCell className="p-1">
                        {eq.image_url ? (
                          <img
                            src={eq.image_url}
                            alt={eq.description || eq.external_code}
                            className="w-10 h-10 object-cover rounded border cursor-pointer hover:opacity-80"
                            onClick={() => window.open(eq.image_url!, '_blank')}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded border border-dashed flex items-center justify-center text-muted-foreground">
                            <ImageIcon className="h-4 w-4" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono font-medium">{eq.external_code}</TableCell>
                      <TableCell className="font-mono">{eq.internal_code}</TableCell>
                      <TableCell className="text-muted-foreground max-w-xs truncate">
                        {eq.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={eq.is_active ? 'success' : 'destructive'}>
                          {eq.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditModal(eq)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => { setDeleteModal({ open: true, equivalency: eq }); setError(''); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-muted-foreground">
                    Pagina {page} de {totalPages} ({total} registros)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      Anterior
                    </Button>
                    {/* Page number buttons */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(pageNum)}
                          className="w-8 h-8 p-0"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modal Crear Equivalencia */}
      <Dialog open={createModal} onOpenChange={(open) => { if (!open) { setCreateModal(false); setError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nueva Equivalencia - {CATEGORY_LABELS[category]}
            </DialogTitle>
            <DialogDescription>
              Agregue una nueva equivalencia de codigo para este proveedor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Codigo Externo</label>
              <Input
                value={createForm.external_code}
                onChange={(e) => setCreateForm({ ...createForm, external_code: e.target.value })}
                placeholder="Codigo del proveedor"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Codigo Interno</label>
              <Input
                value={createForm.internal_code}
                onChange={(e) => setCreateForm({ ...createForm, internal_code: e.target.value })}
                placeholder="Codigo interno del sistema"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Descripcion (opcional)</label>
              <Input
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Descripcion de la equivalencia"
              />
            </div>
            <div>
              <label className="text-sm font-medium">URL de Imagen (opcional)</label>
              <Input
                value={createForm.image_url}
                onChange={(e) => setCreateForm({ ...createForm, image_url: e.target.value })}
                placeholder="https://ejemplo.com/imagen.jpg"
              />
              {createForm.image_url && (
                <div className="mt-2">
                  <img
                    src={createForm.image_url}
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded border"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={isProcessing}>
              {isProcessing ? 'Creando...' : 'Crear Equivalencia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Equivalencia */}
      <Dialog open={editModal.open} onOpenChange={(open) => { if (!open) { setEditModal({ open: false, equivalency: null }); setError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Editar Equivalencia
            </DialogTitle>
            <DialogDescription>Modifique los datos de la equivalencia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Codigo Externo</label>
              <Input
                value={editForm.external_code}
                onChange={(e) => setEditForm({ ...editForm, external_code: e.target.value })}
                placeholder="Codigo del proveedor"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Codigo Interno</label>
              <Input
                value={editForm.internal_code}
                onChange={(e) => setEditForm({ ...editForm, internal_code: e.target.value })}
                placeholder="Codigo interno del sistema"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Descripcion (opcional)</label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Descripcion de la equivalencia"
              />
            </div>
            <div>
              <label className="text-sm font-medium">URL de Imagen (opcional)</label>
              <Input
                value={editForm.image_url}
                onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                placeholder="https://ejemplo.com/imagen.jpg"
              />
              {editForm.image_url && (
                <div className="mt-2">
                  <img
                    src={editForm.image_url}
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded border"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, equivalency: null })} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={isProcessing}>
              {isProcessing ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Eliminar Equivalencia */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => { if (!open) setDeleteModal({ open: false, equivalency: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminar Equivalencia
            </DialogTitle>
            <DialogDescription>
              Esta seguro que desea eliminar esta equivalencia? Esta accion no se puede deshacer.
              {deleteModal.equivalency && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="font-medium font-mono">{deleteModal.equivalency.external_code} → {deleteModal.equivalency.internal_code}</p>
                  {deleteModal.equivalency.description && (
                    <p className="text-sm mt-1">{deleteModal.equivalency.description}</p>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, equivalency: null })} disabled={isProcessing}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isProcessing}>
              {isProcessing ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Importar Equivalencias */}
      <Dialog open={importModal} onOpenChange={(open) => { if (!open) closeImportModal(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importar Equivalencias - {CATEGORY_LABELS[category]}
            </DialogTitle>
            <DialogDescription>
              Pegue los datos a importar en formato CSV o JSON.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Format selector */}
            <div className="flex items-center gap-2">
              <Button
                variant={importFormat === 'csv' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setImportFormat('csv'); setError(''); setImportResult(null); }}
              >
                CSV
              </Button>
              <Button
                variant={importFormat === 'json' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setImportFormat('json'); setError(''); setImportResult(null); }}
              >
                JSON
              </Button>
            </div>

            {/* Format hint */}
            <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
              {importFormat === 'csv' ? (
                <>
                  <p className="font-medium text-foreground mb-1">Formato CSV (una linea por registro):</p>
                  <code className="text-xs">codigo_externo,codigo_interno,descripcion</code>
                  <p className="mt-1">Ejemplo:</p>
                  <code className="text-xs block">PROD-001,INT-001,Producto ejemplo</code>
                  <code className="text-xs block">PROD-002,INT-002,Otro producto</code>
                </>
              ) : (
                <>
                  <p className="font-medium text-foreground mb-1">Formato JSON (arreglo de objetos):</p>
                  <code className="text-xs whitespace-pre">{`[
  { "external_code": "PROD-001", "internal_code": "INT-001", "description": "Producto ejemplo" },
  { "external_code": "PROD-002", "internal_code": "INT-002" }
]`}</code>
                </>
              )}
            </div>

            {/* Text area */}
            <textarea
              className="w-full h-48 p-3 border rounded-md text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder={importFormat === 'csv'
                ? 'PROD-001,INT-001,Descripcion\nPROD-002,INT-002,Otra descripcion'
                : '[{"external_code":"PROD-001","internal_code":"INT-001"}]'
              }
              value={importText}
              onChange={(e) => { setImportText(e.target.value); setError(''); setImportResult(null); }}
            />

            {/* Import result */}
            {importResult && (
              <div className={`p-3 rounded-md text-sm ${importResult.errors.length > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <p className={`font-medium ${importResult.errors.length > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                  Resultado de importacion:
                </p>
                <ul className="mt-1 space-y-1">
                  <li>Importados: {importResult.imported} de {importResult.total_submitted}</li>
                  {importResult.errors.length > 0 && (
                    <li className="text-red-600">
                      Errores ({importResult.errors.length}):
                      <ul className="ml-4 list-disc">
                        {importResult.errors.slice(0, 10).map((err: any, i: number) => (
                          <li key={i} className="text-xs">{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                        ))}
                        {importResult.errors.length > 10 && (
                          <li className="text-xs">... y {importResult.errors.length - 10} errores mas</li>
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImportModal} disabled={isProcessing}>
              {importResult ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!importResult && (
              <Button onClick={handleImport} disabled={isProcessing || !importText.trim()}>
                {isProcessing ? 'Importando...' : 'Importar'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
